"use client";

import * as React from "react";
import { useState } from "react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import { ChevronLeft, Loader2, AlertCircle, X } from "lucide-react";

type CheckoutFooterProps = {
  label?: string;
  value?: string;
  total?: number;
  currency?: string;
  /** Number of selected dates (multi-date mode) */
  dateCount?: number;
  /** Total multiplied by dateCount (multi-date mode) */
  multiDateTotal?: number;
  /** When true, suppress the "N × £perDate" line. Use when per-rate breakdown
   *  is shown elsewhere so the per-date average would be misleading. */
  hidePerDateBreakdown?: boolean;
  showBack?: boolean;
  showContinue?: boolean;
  continueText?: string;
  continueDisabled?: boolean;
  continueLoading?: boolean;
  /** Show pulse animation when button is ready */
  showPulse?: boolean;
  /** Validation message to show when continue is disabled */
  validationMessage?: string;
  /** When true, footer is not sticky - flows after content (for iframe embeds) */
  isEmbedMode?: boolean;
  /** Show cancel button to abort checkout */
  showCancel?: boolean;
  /** Callback when user confirms they want to cancel */
  onCancel?: () => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
  children?: React.ReactNode;
};

function formatPrice(price: number, currency: string = "GBP"): string {
  const symbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const symbol = symbols[currency] || currency;
  return `${symbol}${(price / 100).toFixed(2)}`;
}

function CheckoutFooter({
  label,
  value,
  total,
  currency = "GBP",
  dateCount,
  multiDateTotal,
  hidePerDateBreakdown = false,
  showBack = true,
  showContinue = true,
  continueText = "Continue",
  continueDisabled = false,
  continueLoading = false,
  showPulse = false,
  validationMessage,
  isEmbedMode = true,
  showCancel = false,
  onCancel,
  onBack,
  onContinue,
  className,
  children,
}: CheckoutFooterProps) {
  // State for cancel confirmation dialog
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Auto-pulse when button becomes enabled (for payment step)
  const isReady = !continueDisabled && !continueLoading;
  const shouldPulse = showPulse && isReady;
  const showValidationMessage = continueDisabled && !continueLoading && validationMessage;

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    onCancel?.();
  };

  const handleDismissCancel = () => {
    setShowCancelConfirm(false);
  };

  return (
    <>
      <footer
        className={cn(
          "bg-card border-t border-border py-3 px-1 sm:px-4",
          "shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-50",
          // In embed mode: static positioning, flows after content
          // Otherwise: fixed at bottom of viewport
          isEmbedMode
            ? "relative"
            : "fixed bottom-0 left-0 right-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]",
          className,
        )}
      >
        <div className="container mx-auto">
          {/* Validation message - shown above the buttons on mobile */}
          {showValidationMessage && (
            <div className="flex items-center gap-2 text-destructive text-sm mb-2 px-1">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{validationMessage}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            {/* Left side - Cancel button, Back button, or label/value */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {showCancel && onCancel && (
                <Button
                  variant="ghost"
                  onClick={handleCancelClick}
                  className="flex items-center gap-1 min-h-[44px] touch-manipulation text-muted-foreground hover:text-destructive"
                >
                  <X className="w-4 h-4" />
                  <span className="hidden sm:inline">Cancel</span>
                </Button>
              )}
              {showBack && onBack && (
                <Button
                  variant="ghost"
                  onClick={onBack}
                  className="flex items-center gap-1 min-h-[44px] touch-manipulation"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
              )}
              {label && value && (
                <div className="hidden sm:flex items-center gap-2 text-sm truncate">
                  <span className="text-muted-foreground">{label}:</span>
                  <span className="font-medium text-foreground truncate">
                    {value}
                  </span>
                </div>
              )}
            </div>

            {/* Right side - Total and Continue button */}
            <div className="flex items-center gap-4">
              {total !== undefined && total > 0 && (
                <div className="text-right">
                  {dateCount && dateCount > 1 && multiDateTotal !== undefined ? (
                    <>
                      {!hidePerDateBreakdown && (
                        <p className="text-xs text-muted-foreground tracking-wide hidden sm:block">
                          {dateCount} dates &times;{" "}
                          {formatPrice(Math.round(multiDateTotal / dateCount), currency)}
                        </p>
                      )}
                      <p className="text-lg font-bold text-foreground">
                        {formatPrice(multiDateTotal, currency)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide hidden sm:block">
                        Total
                      </p>
                      <p className="text-lg font-bold text-foreground">
                        {formatPrice(total, currency)}
                      </p>
                    </>
                  )}
                </div>
              )}

              {children}

              {showContinue && onContinue && (
                <div className={cn("relative", shouldPulse && "animate-pulse-subtle")}>
                  <Button
                    onClick={onContinue}
                    disabled={continueDisabled || continueLoading}
                    className={cn(
                      "min-w-[120px] min-h-[48px] touch-manipulation",
                      "transition-all duration-200 ease-out",
                      isReady && "hover:scale-[1.02] active:scale-[0.98]",
                    )}
                    size="lg"
                  >
                    {continueLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        <span className="hidden sm:inline">Loading...</span>
                      </>
                    ) : (
                      continueText
                    )}
                  </Button>
                  {/* Subtle glow effect when ready */}
                  {shouldPulse && (
                    <div className="absolute inset-0 -z-10 rounded-lg bg-primary/20 blur-md animate-pulse" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-sm w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Cancel Checkout?
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to cancel? Your booking progress will be lost.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleDismissCancel}
                className="min-h-[44px]"
              >
                Continue Booking
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmCancel}
                className="min-h-[44px]"
              >
                Yes, Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

type CheckoutFooterSpacerProps = {
  className?: string;
  /** When true, spacer is hidden (not needed when footer is not fixed) */
  isEmbedMode?: boolean;
};

function CheckoutFooterSpacer({ className, isEmbedMode = false }: CheckoutFooterSpacerProps) {
  // In embed mode, footer is not fixed so no spacer needed
  if (isEmbedMode) return null;
  return <div className={cn("h-24", className)} />;
}

CheckoutFooter.Spacer = CheckoutFooterSpacer;

export { CheckoutFooter };
export type { CheckoutFooterProps };
